import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getInsuranceClaims,
  getInsuranceClaimsOverview,
} from '@services/api/dashboard/operations.api'
import { mapInsuranceClaimsResult } from '@utils/dashboardOperations.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import type { SchedulingChartConfig } from '@pages/Dashboard/SchedulingPerformanceDetail/schedulingPerformanceDetail.mockData'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import type { InsuranceClaimsDetailTableRow } from '@utils/dashboardOperations.util'

export type UseInsuranceClaimsOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseInsuranceClaimsResult = {
  loading: boolean
  error: string | null
  data: Awaited<ReturnType<typeof getInsuranceClaims>> | null
  claimsOutstandingMetrics: DashboardMetricItemData[]
  paymentPostingMetrics: DashboardMetricItemData[]
  volumeChartConfig: SchedulingChartConfig
  pipelineRows: InsuranceClaimsDetailTableRow[]
}

function useInsuranceClaimsFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseInsuranceClaimsOptions & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getInsuranceClaims>>
  >
}): UseInsuranceClaimsResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )

  const mapped = useMemo(
    () => mapInsuranceClaimsResult(data, loading, range, date),
    [data, loading, range, date],
  )

  return {
    loading,
    error,
    data,
    ...mapped,
  }
}

export function useInsuranceClaims(
  options: UseInsuranceClaimsOptions,
): UseInsuranceClaimsResult {
  return useInsuranceClaimsFromFetcher({
    ...options,
    fetcher: getInsuranceClaims,
  })
}

export function useInsuranceClaimsOverview(
  options: UseInsuranceClaimsOptions,
): UseInsuranceClaimsResult {
  return useInsuranceClaimsFromFetcher({
    ...options,
    fetcher: getInsuranceClaimsOverview,
  })
}
